import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

export default function CommercialPaintingPage() {
  useEffect(() => {
    document.title = 'Commercial Painting Durbanville | Professional Commercial Painters Western Cape';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional commercial painting services in Durbanville, Western Cape. Expert commercial painters for offices, retail spaces, warehouses, and industrial buildings.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'commercial painting Durbanville, commercial painters Western Cape, office painting, retail painting, industrial painting, warehouse painting');
    }
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/a6f3389a7b5c01394115f9885c4f8522.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold mb-6" style={{ fontFamily: '"Pacifico", serif' }}>
            Commercial <span style={{ color: '#e9f638' }}>PAINTING</span>
          </h1>
          <p className="text-2xl md:text-3xl mb-8 font-light">
            Professional painting solutions for businesses and commercial properties
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Quote
            </a>
            <a 
              href="#services" 
              className="border-2 border-white hover:bg-white hover:text-black text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Our Services
            </a>
          </div>
        </div>
      </section>

      {/* Commercial Painting Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Commercial Painting Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced commercial painters in Durbanville provide comprehensive painting solutions for businesses, offices, and industrial facilities with minimal disruption to your operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Office Buildings</h3>
              <p className="text-gray-600">
                Professional interior and exterior painting for office buildings, corporate headquarters, and business complexes.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-store-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Retail Spaces</h3>
              <p className="text-gray-600">
                Specialized painting services for retail stores, shopping centers, and commercial showrooms to enhance customer experience.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-factory-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Industrial Facilities</h3>
              <p className="text-gray-600">
                Heavy-duty industrial painting for warehouses, factories, and manufacturing facilities with durable coatings.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-hospital-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Healthcare Facilities</h3>
              <p className="text-gray-600">
                Specialized painting for hospitals, clinics, and medical facilities using antimicrobial and easy-clean coatings.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-school-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Educational Buildings</h3>
              <p className="text-gray-600">
                Safe, durable painting solutions for schools, universities, and educational institutions with low-VOC paints.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-hotel-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Hospitality Venues</h3>
              <p className="text-gray-600">
                Professional painting for hotels, restaurants, and entertainment venues to create welcoming environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Commercial Painting */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Professional Commercial Painting?</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Professional Image</h3>
                    <p className="text-gray-600">A well-maintained, freshly painted commercial property projects professionalism and attracts customers and clients.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Minimal Disruption</h3>
                    <p className="text-gray-600">Our experienced team works efficiently around your business schedule to minimize operational disruption.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Durable Solutions</h3>
                    <p className="text-gray-600">Commercial-grade paints and coatings designed to withstand high traffic and heavy use for years.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Cost-Effective Maintenance</h3>
                    <p className="text-gray-600">Regular commercial painting prevents costly repairs and extends the life of your building's surfaces.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20commercial%20building%20in%20Durbanville%20business%20district%20with%20fresh%20professional%20paint%20job%2C%20clean%20facade%2C%20contemporary%20architecture%2C%20well-maintained%20commercial%20property%2C%20South%20African%20business%20environment%2C%20excellent%20presentation&width=600&height=400&seq=commercial-benefits&orientation=landscape"
                alt="Professional commercial painting results"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Painting Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Our Commercial Painting Process</h2>
            <p className="text-xl text-gray-600">
              Streamlined process designed to minimize disruption while delivering exceptional results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                <span className="text-2xl font-bold text-black">1</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Site Assessment</h3>
              <p className="text-gray-600">Comprehensive evaluation of your commercial property and painting requirements.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                <span className="text-2xl font-bold text-black">2</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Project Planning</h3>
              <p className="text-gray-600">Detailed project timeline and scheduling to work around your business operations.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                <span className="text-2xl font-bold text-black">3</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Professional Execution</h3>
              <p className="text-gray-600">Expert painting with minimal disruption using commercial-grade materials and equipment.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                <span className="text-2xl font-bold text-black">4</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Quality Assurance</h3>
              <p className="text-gray-600">Thorough inspection and touch-ups to ensure perfect results and your complete satisfaction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Our Other Painting Services</h2>
            <p className="text-xl text-gray-600">
              Explore our complete range of professional painting services in Durbanville
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/interior-painting" className="group bg-white p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Interior Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Professional interior painting for homes and offices.</p>
            </a>

            <a href="/exterior-painting" className="group bg-white p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Exterior Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Professional exterior painting services for homes and buildings.</p>
            </a>

            <a href="/roof-painting" className="group bg-white p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-2-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Roof Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Specialized roof painting and waterproofing services.</p>
            </a>

            <a href="/residential-painting" className="group bg-white p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Residential Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Complete residential painting solutions for your home.</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Get Your Commercial Painting Quote</h2>
          <p className="text-xl text-gray-300 mb-12">
            Ready to enhance your commercial property? Get a free quote from Durbanville's commercial painting specialists.
          </p>
          <ContactForm />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <TestimonialsSlider />
        </div>
      </section>

      {/* Elfsight AI Chatbot */}
      <script src="https://elfsightcdn.com/platform.js" async></script>
      <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>

      {/* ProveSource Code */}
      <script dangerouslySetInnerHTML={{
        __html: `!function(o,i){window.provesrc&&window.console&&console.error&&console.error("ProveSource is included twice in this page."),provesrc=window.provesrc={dq:[],display:function(){this.dq.push(arguments)}},o._provesrcAsyncInit=function(){provesrc.init({apiKey:"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NvdW50SWQiOiI2NmQzYzc2NjQ3MWQwZDE2NTI2N2VkZDAiLCJpYXQiOjE3MjUxNTUxNzR9.F74G52UprvsGoyXgXsaIMG-PS-c2G7yr84Ut5rrQ4cg",v:"0.0.4"})};var r=i.createElement("script");r.type="text/javascript",r.async=!0,r["ch"+"ar"+"set"]="UTF-8",r.src="https://cdn.provesrc.com/provesrc.js";var e=i.getElementsByTagName("script")[0];e.parentNode.insertBefore(r,e)}(window,document);`
      }} />

      <Footer />
    </>
  );
}
